import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  X, Crown, Check, Star, Heart, Coins, 
  Zap, Gift, Sparkles, Diamond, Loader2,
  CreditCard, Calendar, Shield, Map
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// Premium membership benefits (synced with backend PREMIUM_MEMBERSHIP config)
const MEMBERSHIP_BENEFITS = [
  { 
    id: 'battle_pass', 
    icon: Zap, 
    name: 'Premium Battle Pass', 
    description: 'Full access to all Battle Pass rewards',
    emoji: '⚡'
  },
  { 
    id: 'health_bonus', 
    icon: Heart, 
    name: '+500 Max Health', 
    description: 'Start levels with more hearts',
    emoji: '❤️'
  },
  { 
    id: 'xp_multiplier', 
    icon: Star, 
    name: '1.2x XP Boost', 
    description: '20% more XP from all matches',
    emoji: '✨'
  },
  { 
    id: 'coin_multiplier', 
    icon: Coins, 
    name: '2x Coins', 
    description: 'Double coins from level completion',
    emoji: '🪙'
  },
  { 
    id: 'monthly_stars', 
    icon: Gift, 
    name: '500 Stars Monthly', 
    description: 'Receive 500 stars each month',
    emoji: '⭐'
  },
  { 
    id: 'shop_discount', 
    icon: Shield, 
    name: '10% Shop Discount', 
    description: 'Save on all shop purchases',
    emoji: '💰'
  },
  { 
    id: 'member_world', 
    icon: Map, 
    name: 'Crystal Paradise World', 
    description: 'Exclusive 50-level member world',
    emoji: '💎'
  },
  { 
    id: 'daily_rewards', 
    icon: Calendar, 
    name: 'Daily Premium Rewards', 
    description: 'Extra daily bonus rewards',
    emoji: '🎁'
  },
];

// Exclusive member cosmetics
const MEMBER_COSMETICS = [
  { id: 'member_golden', name: 'Golden Fruits', type: 'skin', emoji: '🌟' },
  { id: 'member_diamond', name: 'Diamond Fruits', type: 'skin', emoji: '💎' },
  { id: 'member_rainbow', name: 'Rainbow Fruits', type: 'skin', emoji: '🌈' },
  { id: 'member_crown', name: 'Crown Frame', type: 'frame', emoji: '👑' },
  { id: 'member_royal', name: 'Royal Theme', type: 'theme', emoji: '🏰' },
];

export function PremiumMembershipModal({ 
  isOpen, 
  onClose, 
  membershipStatus,
  onMembershipUpdate 
}) {
  const [purchasing, setPurchasing] = useState(false);
  const [claimingDaily, setClaimingDaily] = useState(false);
  const [dailyReward, setDailyReward] = useState(null);
  
  // Current membership data
  const isMember = membershipStatus?.is_premium_member || false;
  const expiresDate = membershipStatus?.membership_expires 
    ? new Date(membershipStatus.membership_expires) 
    : null;
  const daysRemaining = expiresDate 
    ? Math.max(0, Math.ceil((expiresDate - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;
  
  // Check if daily reward can be claimed
  const lastDailyClaim = membershipStatus?.last_daily_reward;
  const canClaimDaily = isMember && (!lastDailyClaim || 
    new Date(lastDailyClaim).toDateString() !== new Date().toDateString());

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      let userId = null;
      
      // Get user ID
      if (token) {
        try {
          const userResponse = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            userId = userData.id;
          }
        } catch (e) {
          console.error('Could not get user ID');
        }
      }
      
      if (!userId) {
        toast.error('Please log in to purchase membership');
        setPurchasing(false);
        return;
      }
      
      // Create checkout session for membership
      const response = await fetch(`${API_URL}/api/stripe/create-membership-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin + window.location.pathname,
          user_id: userId,
        }),
      });
      
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Could not create checkout session');
      }
    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error('Purchase failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleClaimDaily = async () => {
    if (!canClaimDaily) return;
    
    setClaimingDaily(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      // Get user ID from token
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.user_id;
      
      const response = await fetch(`${API_URL}/api/stripe/claim-daily-premium-reward/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setDailyReward(data.rewards);
        toast.success('Daily reward claimed!');
        if (onMembershipUpdate) onMembershipUpdate();
      } else {
        toast.error(data.detail || 'Could not claim reward');
      }
    } catch (error) {
      console.error('Claim failed:', error);
      toast.error('Failed to claim reward');
    } finally {
      setClaimingDaily(false);
    }
  };

  // Verify membership payment on mount (if returning from checkout)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const membershipSuccess = urlParams.get('membership');
    const sessionId = urlParams.get('session_id');
    
    if (membershipSuccess === 'success' && sessionId) {
      verifyMembership(sessionId);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const verifyMembership = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/stripe/verify-membership/${sessionId}`);
      const data = await response.json();
      
      if (data.success && data.is_premium_member) {
        toast.success(`🎉 Welcome to Premium! You received ${data.stars_credited} bonus stars!`);
        if (onMembershipUpdate) onMembershipUpdate();
      }
    } catch (error) {
      console.error('Membership verification failed:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      <Card 
        className="relative w-full max-w-lg max-h-[90vh] overflow-hidden bg-gradient-to-b from-purple-900 via-violet-900 to-indigo-900 border-2 border-purple-400 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="membership-modal"
      >
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-pink-500/20 rounded-full blur-3xl" />
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white animate-pulse"
              style={{
                width: Math.random() * 3 + 1 + 'px',
                height: Math.random() * 3 + 1 + 'px',
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + '%',
                opacity: Math.random() * 0.5 + 0.2,
                animationDelay: Math.random() * 2 + 's'
              }}
            />
          ))}
        </div>
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5 text-white/80" />
        </button>
        
        {/* Header */}
        <div className="relative pt-6 pb-4 px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-3 bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 rounded-2xl shadow-lg shadow-orange-500/30">
            <Crown className="w-9 h-9 text-white" />
          </div>
          <h2 className="font-display text-2xl font-bold text-white mb-1">
            Premium Membership
          </h2>
          <p className="text-purple-200 text-sm">
            {isMember ? `Active • ${daysRemaining} days remaining` : 'Unlock exclusive benefits'}
          </p>
          
          {/* Status Badge */}
          {isMember && (
            <Badge className="mt-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-1">
              <Check className="w-4 h-4 mr-1" /> ACTIVE MEMBER
            </Badge>
          )}
        </div>
        
        {/* Content */}
        <div className="px-6 pb-6 overflow-y-auto max-h-[50vh]">
          {/* Daily Reward Claim (for members) */}
          {isMember && (
            <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Daily Reward</h3>
                    <p className="text-xs text-purple-200">
                      {canClaimDaily ? 'Ready to claim!' : 'Claimed today'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleClaimDaily}
                  disabled={!canClaimDaily || claimingDaily}
                  size="sm"
                  className={`font-bold ${
                    canClaimDaily 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' 
                      : 'bg-gray-600 cursor-not-allowed'
                  }`}
                >
                  {claimingDaily ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : canClaimDaily ? (
                    'Claim'
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              {/* Show claimed reward */}
              {dailyReward && (
                <div className="mt-3 p-2 rounded-lg bg-black/30 flex items-center justify-center gap-4 text-sm text-white">
                  <span>⭐ +{dailyReward.stars}</span>
                  <span>🪙 +{dailyReward.coins}</span>
                  {dailyReward.booster && <span>🎁 +1 {dailyReward.booster}</span>}
                </div>
              )}
            </div>
          )}
          
          {/* Benefits Grid */}
          <h3 className="font-bold text-white mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-300" />
            Membership Benefits
          </h3>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {MEMBERSHIP_BENEFITS.map((benefit) => (
              <div 
                key={benefit.id}
                className={`p-3 rounded-xl border transition-all ${
                  isMember 
                    ? 'bg-purple-500/20 border-purple-400/50' 
                    : 'bg-white/5 border-white/10'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-xl">{benefit.emoji}</span>
                  <div>
                    <h4 className="font-bold text-white text-xs leading-tight">{benefit.name}</h4>
                    <p className="text-[10px] text-purple-200 leading-tight mt-0.5">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Exclusive Cosmetics */}
          <h3 className="font-bold text-white mb-3 flex items-center gap-2">
            <Diamond className="w-4 h-4 text-purple-300" />
            Exclusive Cosmetics
          </h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {MEMBER_COSMETICS.map((cosmetic) => (
              <div 
                key={cosmetic.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  isMember 
                    ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 border-purple-400/50' 
                    : 'bg-white/5 border-white/10'
                }`}
              >
                <span className="text-lg">{cosmetic.emoji}</span>
                <span className="text-xs text-white font-medium">{cosmetic.name}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer / Purchase Section */}
        <div className="p-4 bg-black/30 border-t border-purple-500/30">
          {!isMember ? (
            <div className="space-y-3">
              {/* Price Display */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-3xl font-bold text-white">€11,99</span>
                  <span className="text-purple-200">/month</span>
                </div>
                <p className="text-xs text-purple-300 mt-1">Cancel anytime</p>
              </div>
              
              {/* Purchase Button */}
              <Button
                onClick={handlePurchase}
                disabled={purchasing}
                className="w-full h-12 font-bold text-lg bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 hover:from-amber-600 hover:via-orange-600 hover:to-pink-600 shadow-lg shadow-orange-500/30"
                data-testid="purchase-membership-btn"
              >
                {purchasing ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Get Premium Now
                  </>
                )}
              </Button>
              
              <p className="text-center text-xs text-purple-300">
                Secure payment via Stripe
              </p>
            </div>
          ) : (
            <div className="text-center space-y-2">
              <p className="text-purple-200 text-sm">
                Thank you for being a Premium member!
              </p>
              <p className="text-xs text-purple-300">
                Membership expires: {expiresDate?.toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// Premium badge component for headers/UI
export function PremiumBadge({ isPremium, onClick, compact = false }) {
  if (!isPremium) return null;
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-md transition-all hover:scale-105 border border-amber-400 ${
        compact ? 'px-1.5 py-0.5' : 'px-2 py-1'
      }`}
      data-testid="premium-badge"
    >
      <Crown className={`fill-white text-white ${compact ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'}`} />
      {!compact && <span className="text-xs font-bold text-white">VIP</span>}
    </button>
  );
}

export default PremiumMembershipModal;
